import { ReactNode, useCallback, useState } from 'react';

import { useAtomValue } from 'jotai';

import { useUserSettingsWithDefaults } from '@hooks';
import { useFavoriteSwings, useElvaSwings, useUpdateUserSettings, useSwing } from '@queries';
import { activeUserIdAtom } from '@store';
import { Box, Button, ModalBase, ModalHeader, Tabs, u } from '@ui';

import { ElvaSwingsTab } from '../../ComparisonTabs/ElvaSwingsTab';
import { MySwingsTab } from '../../ComparisonTabs/MySwingsTab';
import { SwingFilter, SwingFilterBox, SwingFilterFooter } from '../../SwingFilter/SwingFilter';
import { useSwingFilter } from '../../SwingFilter/useSwingFilter';

import * as css from './ComparisonSwingModal.css';

const COMPARISON_TABS = [{ id: 'My swings' }, { id: 'ELVA swings' }];

type ComparisonSwingModalProps = { open: boolean; onDismiss: () => void };

type SelectedSwingProps = {
    swingId: string;
    clearSwing: () => void;
};

const SelectedSwing = ({ swingId, clearSwing }: SelectedSwingProps) => {
    const { data: swing } = useSwing(swingId);
    return (
        <Box px={u(2)}>
            <Button size="small" variant="secondary" rounded icon="close" onClick={clearSwing}>
                {swing?.name ?? 'Loading...'}
            </Button>
        </Box>
    );
};

export const ComparisonSwingModal = ({ open, onDismiss }: ComparisonSwingModalProps): ReactNode => {
    const [tab, setTab] = useState(COMPARISON_TABS[0].id);
    const activeUserId = useAtomValue(activeUserIdAtom);
    const { data: userSettings } = useUserSettingsWithDefaults(activeUserId);
    const { comparisonSwingId } = userSettings;
    const { mutateAsync: updateUserSettings } = useUpdateUserSettings(activeUserId);

    const [filter, setFilter, filterQuery] = useSwingFilter();

    // Fetch only the count
    const showingMySwings = tab === COMPARISON_TABS[0].id;
    const showingElvaSwings = tab === COMPARISON_TABS[1].id;
    const { count: favoriteSwingsCount = 0 } = useFavoriteSwings(0, filterQuery, activeUserId);
    const { count: elvaSwingsCount = 0 } = useElvaSwings(0, filterQuery);
    const swingCount = showingMySwings ? favoriteSwingsCount : elvaSwingsCount;

    const setComparisonSwing = useCallback(
        async (uuid: string) => {
            await updateUserSettings({ comparisonSwingId: comparisonSwingId === uuid ? null : uuid });
            onDismiss();
        },
        [comparisonSwingId, onDismiss, updateUserSettings],
    );

    const clearComparisonSwing = useCallback(async () => {
        await updateUserSettings({ comparisonSwingId: null });
        onDismiss();
    }, [onDismiss, updateUserSettings]);

    return (
        <ModalBase onDismiss={onDismiss} open={open} variant="partial">
            <ModalHeader>{'Select comparison swing'}</ModalHeader>
            <Box className={css.swingModalBody}>
                <Box className={css.swingModalFilter}>
                    <SwingFilterBox variant="outline">
                        <SwingFilter value={filter} onChange={setFilter} />
                        <SwingFilterFooter swingCount={swingCount} />
                    </SwingFilterBox>
                </Box>
                {comparisonSwingId && <SelectedSwing swingId={comparisonSwingId} clearSwing={clearComparisonSwing} />}
                <Tabs items={COMPARISON_TABS} activeId={tab} onClick={(id) => setTab(id)} />
                {showingMySwings && <MySwingsTab filter={filterQuery} setComparisonSwing={setComparisonSwing} />}
                {showingElvaSwings && <ElvaSwingsTab filter={filterQuery} setComparisonSwing={setComparisonSwing} />}
            </Box>
        </ModalBase>
    );
};
